import { ChevronDown, ChevronUp } from "lucide-react";
import { useState, type ReactNode } from "react";

// Collapsible homepage section ("Jak działa Chariot?", "Ostatnie wyszukiwania",
// "Przykładowe raporty"). The header is ONE full-width button — title and
// chevron share the hit area, because a small chevron alone is an easy-to-miss
// target. Sections collapse to save space and reopen at any time; they are
// never removable (decision 2026-08-16, replaces the old dismiss-X pattern).
//
// Collapsed state persists per browser via localStorage (not the user
// profile): section chrome is not data, and the worst case of a lost flag is
// seeing the section expanded on a new device.
const STORAGE_PREFIX = "chariot:collapse-section:";

function useCollapsedSection(storageKey: string): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(STORAGE_PREFIX + storageKey) === "1";
    } catch {
      return false;
    }
  });
  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(STORAGE_PREFIX + storageKey, next ? "1" : "0");
      } catch {
        // Private mode / storage disabled — persist for this session only.
      }
      return next;
    });
  };
  return [collapsed, toggle];
}

export function CollapsibleSection({
  storageKey,
  title,
  children,
  className,
}: {
  storageKey: string;
  title: string;
  children: ReactNode;
  className?: string;
}) {
  const [collapsed, toggle] = useCollapsedSection(storageKey);
  const headingId = `section-${storageKey}`;
  const contentId = `section-content-${storageKey}`;

  return (
    <section aria-labelledby={headingId} className={className}>
      {/* -mx-1.5 + px-1.5: the title text starts flush with the section content
          below (x=0) while the hover/focus background gets a small symmetric
          inset on both sides instead of starting left of the text. */}
      <button
        type="button"
        aria-expanded={!collapsed}
        aria-controls={contentId}
        aria-label={
          collapsed
            ? `Rozwiń sekcję „${title}”`
            : `Zwiń sekcję „${title}”`
        }
        onClick={toggle}
        className="-mx-1.5 flex w-[calc(100%+0.75rem)] items-center justify-between rounded-lg px-1.5 py-1 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <h2
          id={headingId}
          className="font-sans text-base font-semibold tracking-normal text-foreground"
        >
          {title}
        </h2>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground">
          {collapsed ? (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronUp className="h-4 w-4" aria-hidden="true" />
          )}
        </span>
      </button>
      {!collapsed && <div id={contentId}>{children}</div>}
    </section>
  );
}
